'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { imageSrc } from '@/utils/runtimeOrg';

type FooterLink = {
  title: string;
  url: string;
};

type FooterProps = {
  cfg: {
    name: string;
    logo: string;
    footer?: {
      description?: string;
      links?: FooterLink[];
      copyright?: string;
    };
    // Accepted for forward-compatibility (callers may pass org contact details);
    // currently not rendered by the footer.
    contact?: unknown;
  };
};

const isExternal = (url: string) =>
  /^https?:\/\//i.test(url) ||
  url.startsWith('mailto:') ||
  url.startsWith('tel:');

export default function Footer({ cfg }: FooterProps) {
  const footer = cfg.footer ?? {};
  const links: FooterLink[] = footer.links ?? [];

  // Auto-current year: any 4-digit year in the configured copyright string
  // is replaced at render time, so configs don't need a yearly edit.
  const copyright = footer.copyright
    ? footer.copyright.replace(/\b(19|20)\d{2}\b/, String(new Date().getFullYear()))
    : null;

  return (
    <footer className="bg-[#f9f9f9] text-[#1b2d2a] text-sm">
      <div className="w-full flex flex-col lg:flex-row justify-between items-start gap-6 px-4 sm:px-6 lg:px-8 xl:px-10 py-8 space-y-8 lg:space-y-0">
        <div className="flex flex-col space-y-4 max-w-3xl">
          <div className="flex items-center w-[200px] space-x-2">
            <Link href="/" aria-label="Home">
              <Image
                src={imageSrc(cfg.logo)}
                alt={cfg.name}
                width={148}
                height={64}
                className="w-full h-full"
                priority={false}
              />
            </Link>
          </div>

          {footer.description ? (
            <p className="text-[#1b2d2a] leading-relaxed">
              {footer.description}
            </p>
          ) : null}

          {!!links.length && (
            <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2">
              {links.map((link) =>
                isExternal(link.url) ? (
                  <a
                    key={link.title}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline hover:text-[var(--color-accent)] transition-colors"
                  >
                    {link.title}
                  </a>
                ) : (
                  <Link
                    key={link.title}
                    href={link.url}
                    className="hover:underline hover:text-[var(--color-accent)] transition-colors"
                  >
                    {link.title}
                  </Link>
                )
              )}
            </div>
          )}
        </div>
      </div>

      <hr className="border-t border-gray-200 mx-auto" />

      {copyright ? (
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 py-4 text-xs text-[#1b2d2a]">
          {copyright}
        </div>
      ) : null}
    </footer>
  );
}
